import * as React from 'react';
import '../commonStyles.scss';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { getChecksumReport, clearChecksumReport } from '../../../redux/actions/backoffice/checksum-report-actions';
import {
    getChecksum,
    getChecksumHashAlgorithms,
    getChecksumLoading,
    getTotalChecksumItems,
} from '../../../redux/selectors/backoffice/checksum-selectors';
import { ContentComponent } from '../../../Components/ContentComponent/ContentComponent';
import {
    Button,
    GetProp,
    Input,
    Select,
    Table,
    TablePaginationConfig,
    TableProps,
} from 'antd';
import * as i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { IChecksumData } from '../../../helpers/interfaces';
import { useCallback, useEffect, useState } from 'react';
import { getServerError } from '../../../redux/selectors/backoffice/error-selectors';
import { ErrorComponent } from '../../Errors/ErrorComponent';
import { HeaderTitleComponent } from '../../../Components/ContentComponent/HeaderTitleComponent';
import { ChecksumColumns } from './ChecksumColumns';
import { SearchOutlined } from '@ant-design/icons';
import { getDateOfLastRefresh } from '../../../redux/selectors/user-selectors';
import { getAuthenticatedFetchOptions, getLoginRequestHeader } from "../../../utils";
import { SERVER_URL } from 'utils/config';

interface TableParams {
    pagination: TablePaginationConfig;
    sortField?: string;
    sortOrder?: string;
    filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

interface IProps {
    data: IChecksumData[];
    getReport: Function;
    clearReport: Function;
    t: i18next.TFunction;
    totalItems: number;
    changePagination: Function;
    pagingInfo: any;
    error: string;
    loading: boolean;
    hashAlgorithms: string[];
}

const MAX_PAGE_SIZE = 50;

export const fetchReport = async (url: string, filename = 'checksum.csv') => {
    const config = getAuthenticatedFetchOptions({
        method: 'GET',
        headers: {
            ...getLoginRequestHeader(),
            'Accept': 'text/csv',
        }
    });

    const response = await fetch(url, config);
    if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
    }
};

const ChecksumReportComponent = (props: IProps) => {
    const { t } = useTranslation();
    const { data, getReport, clearReport, totalItems, error, loading } = props;
    const columns: any = ChecksumColumns();
    const [tableHeight, setTableHeight] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [algorithmType, setAlgorithmType] = useState<string[]>([]);
    const totalWidth = columns.reduce((total: any, col: any) => total + (col.width || 0), 0);
    const [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: MAX_PAGE_SIZE,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            total: totalItems,
        },
    });

    const calculateTableHeight = useCallback(() => {
        const viewportHeight = window.innerHeight;
        const headerHeight = 64;
        const height = Math.max(400, viewportHeight * 0.8 - headerHeight);
        setTableHeight(height);
    }, []);

    const handleExportCSV = useCallback(() => {
        const baseUrl = `${SERVER_URL}/game-certified-hash`;
        const params = new URLSearchParams();

        params.append('pageNumber', tableParams.pagination.current?.toString() || '1');
        params.append('pageSize', tableParams.pagination.pageSize?.toString() || MAX_PAGE_SIZE.toString());

        if (tableParams.sortField) {
            params.append('sortBy', tableParams.sortField);
        }
        if (tableParams.sortOrder) {
            params.append('order', tableParams.sortOrder === 'ascend' ? 'ASCENDING' : 'DESCENDING');
        }

        if (algorithmType && algorithmType.length > 0) {
            algorithmType.forEach((alg) => params.append('hashAlgorithm', alg));
        }

        if (searchText) {
            params.append('search', searchText);
        }

        const exportUrl = `${baseUrl}?${params.toString()}`;
        fetchReport(exportUrl, 'checksum-report.csv');
    }, [tableParams, algorithmType, searchText]);

    const handleTableChange: TableProps['onChange'] = (pagination, filters, sorter) => {
        const nextSortField = Array.isArray(sorter) ? sorter[0]?.field : sorter?.field;
        const nextSortOrder = Array.isArray(sorter) ? sorter[0]?.order : sorter?.order;

        const effectiveSortField = (nextSortField as string) || (tableParams.sortField as string) || 'id';
        const effectiveSortOrder = (nextSortOrder as string) || (tableParams.sortOrder as string) || 'DESCEND';

        setTableParams({
            pagination,
            filters,
            sortField: effectiveSortField,
            sortOrder: effectiveSortOrder,
        });

        getReport({
            pagingInfo: {
                pageNumber: pagination.current,
                pageSize: pagination.pageSize,
                sortBy: effectiveSortField,
                order: effectiveSortOrder === 'ascend' ? 'ASCENDING' : effectiveSortOrder === 'descend' ? 'DESCENDING' : 'DESCENDING',
            },
            search: searchText,
            algorithmType,
        });
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        
        clearReport();
        
        setTableParams(prevParams => ({
            ...prevParams,
            pagination: {
                ...prevParams.pagination,
                current: 1,
            },
        }));
        
        getReport({
            pagingInfo: {
                pageNumber: 1,
                pageSize: tableParams.pagination.pageSize,
                sortBy: 'id',
                order: 'ASCENDING',
            },
            search: value,
            algorithmType,
        });
    };

    const handleAlgorithmChange = (values: string[]) => {
        setAlgorithmType(values);
        
        clearReport();
        
        setTableParams(prevParams => ({
            ...prevParams,
            pagination: {
                ...prevParams.pagination,
                current: 1,
            },
        }));
        
        getReport({
            pagingInfo: {
                pageNumber: 1,
                pageSize: tableParams.pagination.pageSize,
                sortBy: 'id',
                order: 'ASCENDING',
            },
            search: searchText,
            algorithmType: values,
        });
    };

    useEffect(() => {
        getReport({
            pagingInfo: {
                pageNumber: 1,
                pageSize: MAX_PAGE_SIZE,
                sortBy: 'id',
                order: 'ASCENDING',
            },
            search: searchText,
            algorithmType,
        });
        // eslint-disable-next-line
    }, [getReport]);

    useEffect(() => {
        setTableParams((prevParams) => ({
            ...prevParams,
            pagination: {
                ...prevParams.pagination,
                total: totalItems,
            },
        }));
    }, [totalItems]);

    useEffect(() => {
        if (data && data.length === 0 && !loading) {
            setTableParams(prevParams => ({
                ...prevParams,
                pagination: {
                    ...prevParams.pagination,
                    current: 1,
                },
            }));
        }
    }, [data, loading]);

    useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    return (
        <ContentComponent
            header={<HeaderTitleComponent title={t('critical_checksum')} />}
            innerContent={
                error ? (
                    <ErrorComponent error={error} />
                ) : (
                    <div className="casino_performance-table-container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <div className="flex items-center gap-6 mb-4">
                            <div className="flex justify-between w-full">
                                <div className="flex w-full">
                                    <div className="w-72 mr-4">
                                        <Select
                                            className="w-full"
                                            mode="multiple"
                                            value={algorithmType}
                                            onChange={handleAlgorithmChange}
                                            placeholder="Select hash algorithm(s)"
                                            allowClear
                                        >
                                            <Select.Option value="SHA-1">SHA-1</Select.Option>
                                            <Select.Option value="SHA-256">SHA-256</Select.Option>
                                            <Select.Option value="MD-5">MD-5</Select.Option>
                                        </Select>
                                    </div>
                                    <div className="w-96">
                                        <Input
                                            className="w-full"
                                            placeholder="Search by Game ID, Name, or File Name"
                                            prefix={<SearchOutlined />}
                                            allowClear
                                            onChange={(e) => handleSearch(e.target.value)}
                                            value={searchText}
                                        />
                                    </div>
                                </div>
                            </div>
                            <Button
                                onClick={handleExportCSV}
                                disabled={loading}
                            >
                                Export CSV
                            </Button>
                        </div>
                        <div 
                            className="rounded-lg border border-gray-200 checksum-table-container"
                            style={{
                                height: `${tableHeight + 60}px`, 
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                flex: 1,
                                maxHeight: '80vh'
                            }}
                        >
                            <Table
                                key={`${algorithmType}-${searchText}`}
                                style={{
                                    minWidth: '100%',
                                    height: '100%',
                                }}
                                className={'casino_performance-table'}
                                dataSource={data}
                                scroll={{ x: totalWidth, y: tableHeight - 60 }}
                                columns={columns}
                                onChange={handleTableChange}
                                pagination={{
                                    ...tableParams.pagination,
                                    position: ['bottomRight'],
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                }}
                                rowKey={(record) => `${record.gameId.toString()}`}
                                loading={loading}
                            />
                        </div>
                    </div>
                )
            }
        />
    );
};

const mapStateToProps = (state: any) => ({
    data: getChecksum(state),
    totalItems: getTotalChecksumItems(state),
    error: getServerError(state),
    dateOfLastRefresh: getDateOfLastRefresh(state),
    loading: getChecksumLoading(state),
    hashAlgorithms: getChecksumHashAlgorithms(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getReport: (data: any) => dispatch(getChecksumReport(data)),
    clearReport: () => dispatch(clearChecksumReport()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChecksumReportComponent);
